import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccordionItemComponent, Badge, ButtonGroupComponent, ZagSwitch, type ButtonGroupOption } from '@zag/ui';
import type { EntityType } from '@zag/auth/models';
import {
  isScopeablePermission,
  type PermissionScopeChange,
  type PermissionScopeSelection,
  type PermissionScopeState,
} from './permission-scope-order';

/**
 * Permission item shape accepted by the checklist group.
 * Re-exported by `PermissionChecklistComponent` to preserve its public API.
 */
export interface PermissionChecklistItem {
  id: string;
  description?: string | null;
  featureKey?: string | null;
  scopeEntityType?: EntityType | null;
  requiresPermissionId?: string | null;
}

// @story: libs/storybook/stories/modules/auth/permission-checklist.stories.ts
@Component({
  selector: 'sc-permission-checklist-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AccordionItemComponent, Badge, ButtonGroupComponent, ZagSwitch, TranslatePipe],
  host: {
    '[class.sc-permission-checklist-group]': 'true',
  },
  template: `
    <zag-accordion-item
      [title]="resource()"
      icon="key"
      [badge]="selectedInGroup()"
      [expanded]="isExpanded()"
      (expandedChange)="expanded.set($event)"
    >
      <zag-switch
        slot="actions"
        class="sc-permission-checklist-group__toggle"
        [checked]="isGroupGranted()"
        [ariaLabel]="'permissionChecklist.toggleGroup' | translate: { resource: resource() }"
        (checkedChange)="onGroupToggle()"
        (click)="$event.stopPropagation()"
      />
      <div class="sc-permission-checklist-group__list">
        @for (perm of permissions(); track perm.id) {
          <div class="sc-permission-checklist-group__item">
            <span class="sc-permission-checklist-group__label">
              @if (perm.description) {
                <span class="sc-permission-checklist-group__desc">{{ perm.description }}</span>
              }
              <zag-badge class="sc-permission-checklist-group__key" color="neutral">{{ perm.id }}</zag-badge>
            </span>
            <div class="sc-permission-checklist-group__control">
              @if (isScopeable(perm)) {
                <zag-button-group
                  [options]="scopeOptions()"
                  [value]="scopeOf(perm.id)"
                  orientation="horizontal"
                  [iconOnly]="true"
                  [bordered]="false"
                  [ariaLabel]="'permissionChecklist.scopeFor' | translate: { permission: perm.id }"
                  (selectionChange)="onScopeSelect(perm.id, $event ?? 'inactive')"
                />
              } @else {
                <zag-switch
                  class="sc-permission-checklist-group__item-toggle"
                  [checked]="isGranted(perm.id)"
                  [ariaLabel]="perm.id"
                  [attr.aria-label]="perm.id"
                  (checkedChange)="onSwitchToggle(perm.id)"
                  (click)="$event.stopPropagation()"
                />
              }
            </div>
          </div>
        }
      </div>
    </zag-accordion-item>
  `,
  styles: [
    `
      .sc-permission-checklist-group {
        display: block;
      }

      .sc-permission-checklist-group__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .sc-permission-checklist-group__item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-xs) var(--spacing-sm);
        border-radius: var(--border-radius-sm);
      }

      .sc-permission-checklist-group__label {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--spacing-xs);
        flex: 1;
        min-inline-size: 0;
        overflow-wrap: anywhere;
      }

      .sc-permission-checklist-group__desc {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-normal);
        color: var(--text-color-primary);
      }

      .sc-permission-checklist-group__key {
        max-inline-size: 100%;
        white-space: normal;
        overflow-wrap: anywhere;
      }

      .sc-permission-checklist-group__control {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: flex-end;
        margin-inline-start: auto;
      }
    `,
  ],
})
export class PermissionChecklistGroupComponent {
  /** Resource prefix heading this group (e.g. `users`). */
  readonly resource = input.required<string>();

  /** Permissions in this group. */
  readonly permissions = input.required<PermissionChecklistItem[]>();

  /** Currently checked permission ids (legacy boolean mode, e.g. API keys). */
  readonly selectedIds = input<Set<string>>(new Set());

  /** Pending scope grants; presence switches the group into tri-state mode. */
  readonly selectedScopes = input<PermissionScopeSelection | undefined>(undefined);

  /** When true the group renders expanded regardless of its toggle state. */
  readonly isSearching = input(false);

  /** Emitted when a switch row is activated (legacy boolean mode). */
  readonly permissionToggle = output<string>();

  /** Emitted when a tri-state row selects a scope. */
  readonly scopeChange = output<PermissionScopeChange>();

  /** Emitted when the group toggle is activated (legacy boolean mode). */
  readonly groupToggle = output<void>();

  /** Emitted with the group target scope (tri-state mode). */
  readonly groupScopeChange = output<PermissionScopeState>();

  /** Manual expand state. Empty by default — groups start collapsed. */
  protected readonly expanded = signal(false);

  protected readonly isExpanded = computed(() => this.isSearching() || this.expanded());

  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly scopeOptions = computed<ButtonGroupOption<PermissionScopeState>[]>(() => {
    this.currentLang();
    return [
      { value: 'inactive', label: this.translate.instant('permissionChecklist.scope.inactive'), icon: 'block' },
      { value: 'scoped', label: this.translate.instant('permissionChecklist.scope.scoped'), icon: 'tune' },
      { value: 'full', label: this.translate.instant('permissionChecklist.scope.full'), icon: 'check' },
    ];
  });

  protected readonly isScopesMode = computed(() => this.selectedScopes() !== undefined);

  protected isScopeable(perm: PermissionChecklistItem): boolean {
    return this.isScopesMode() && isScopeablePermission(perm);
  }

  protected scopeOf(id: string): PermissionScopeState {
    return this.selectedScopes()?.get(id) ?? 'inactive';
  }

  protected isGranted(id: string): boolean {
    return this.isScopesMode() ? (this.selectedScopes()?.has(id) ?? false) : this.selectedIds().has(id);
  }

  protected onScopeSelect(id: string, scope: PermissionScopeState): void {
    this.scopeChange.emit({ id, scope });
  }

  protected onSwitchToggle(id: string): void {
    if (!this.isScopesMode()) {
      this.permissionToggle.emit(id);
      return;
    }
    const scope: PermissionScopeState = this.isGranted(id) ? 'inactive' : 'full';
    this.scopeChange.emit({ id, scope });
  }

  protected onGroupToggle(): void {
    if (!this.isScopesMode()) {
      this.groupToggle.emit();
      return;
    }
    this.groupScopeChange.emit(this.isGroupGranted() ? 'inactive' : 'full');
  }

  protected selectedInGroup(): number {
    return this.permissions().filter(p => this.isGranted(p.id)).length;
  }

  protected isGroupGranted(): boolean {
    const perms = this.permissions();
    if (perms.length === 0) return false;
    return perms.every(p => this.isGranted(p.id));
  }
}
